import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

/**
 * FeatureFlagsContext — fetches admin-controlled feature flags once on mount
 * and exposes a useFeatureFlag(key) hook for client-side gating.
 *
 * Admins (detected server-side via admin session cookie) automatically receive
 * `enabled: true` for every flag (preview mode) so they can validate features
 * before rolling them out to users.
 */

const FeatureFlagsContext = createContext({
  flags: {},
  adminPreview: false,
  loaded: false,
  refresh: async () => {},
});

const FLAGS_CACHE_KEY = 'home_cache_feature_flags';

const readCachedFlags = () => {
  try { return JSON.parse(localStorage.getItem(FLAGS_CACHE_KEY) || 'null'); }
  catch { return null; }
};

export function FeatureFlagsProvider({ children }) {
  // Seed from the last-known flags so gated UI renders on first paint.
  const [cached] = useState(readCachedFlags);
  const [flags, setFlags] = useState(cached?.flags || {});
  const [adminPreview, setAdminPreview] = useState(!!cached?.admin_preview);
  const [loaded, setLoaded] = useState(!!cached);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feature-flags`, { withCredentials: true });
      setFlags(res.data?.flags || {});
      setAdminPreview(!!res.data?.admin_preview);
      try {
        localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify({
          flags: res.data?.flags || {},
          admin_preview: !!res.data?.admin_preview,
        }));
      } catch { /* quota */ }
    } catch (err) {
      // Network error or backend unavailable — treat all flags as off
      // so users never see partially-shipped features by accident.
      setFlags({});
      setAdminPreview(false);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Re-fetch when the tab regains focus so admin toggles applied in
    // another tab take effect immediately without a hard reload.
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) refresh();
    });
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, adminPreview, loaded, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

export function useFeatureFlag(key) {
  const { flags, loaded } = useContext(FeatureFlagsContext);
  return { enabled: !!flags[key], loaded };
}
